/**
 * UIAnimations — shared scene-transition + modal + flourish helpers.
 *
 * Button construction lives in src/ui/PillButton.ts (the canonical CTA).
 * This file is now scoped to motion utilities only.
 */

import Phaser from 'phaser';
import { AudioManager } from './AudioManager';
import { MOTION, COLORS } from '../ui/theme';


// ─── Modal Animations ────────────────────────────────────────────────────────

/**
 * Animate modal opening — slam in from above with Back ease, dust burst.
 */
export function modalOpen(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.Image | Phaser.GameObjects.Container,
  delay = 0,
  duration = MOTION.dur.modalIn
): void {
  target.setScale(0.5);
  target.setAlpha(0);
  const baseScale = (target as any).scale ?? 1;

  scene.tweens.add({
    targets: target,
    scaleX: baseScale * MOTION.scale.modalOvershoot,
    scaleY: baseScale * MOTION.scale.modalOvershoot,
    alpha: 1,
    duration: duration * 0.75,
    delay,
    ease: MOTION.ease.modalIn,
    onComplete: () => {
      scene.tweens.add({
        targets: target,
        scaleX: baseScale,
        scaleY: baseScale,
        duration: duration * 0.25,
        ease: 'Power2',
      });
    },
  });

  // Prefer v2 heavy curtain drop, fall back to legacy
  const curtainSfx = scene.cache.audio.exists('sfx_curtain_heavy_down')
    ? 'sfx_curtain_heavy_down' : 'sfx_curtain_drop';
  const play = () => {
    AudioManager.getInstance().playSFX(scene, curtainSfx, { volume: 0.6 });
    AudioManager.getInstance().playSFX(scene, 'sfx_modal_open', { volume: 0.4 });
  };
  if (delay === 0) play();
  else scene.time.delayedCall(delay, play);
}

/**
 * Animate modal closing — shrink + fade + sound.
 */
export function modalClose(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.Image | Phaser.GameObjects.Container,
  onComplete?: () => void,
  duration: number = MOTION.dur.modalOut
): void {
  // Prefer v2 curtain up, fall back to legacy modal close
  const sfxKey = scene.cache.audio.exists('sfx_curtain_heavy_up')
    ? 'sfx_curtain_heavy_up' : 'sfx_modal_close';
  AudioManager.getInstance().playSFX(scene, sfxKey, { volume: 0.5 });
  scene.tweens.add({
    targets: target,
    scaleX: 0.85,
    scaleY: 0.85,
    alpha: 0,
    duration,
    ease: MOTION.ease.modalOut,
    onComplete: () => onComplete?.(),
  });
}

// ─── Smash-In Animations ─────────────────────────────────────────────────────

/**
 * Element smashes in from scale 0 with overshoot.
 */
export function smashIn(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject,
  delay = 0,
  duration = 350,
  targetScale = 1
): void {
  (target as any).setScale?.(0);
  (target as any).setAlpha?.(0);
  scene.tweens.add({
    targets: target,
    scaleX: targetScale,
    scaleY: targetScale,
    alpha: 1,
    duration,
    delay,
    ease: 'Back.easeOut',
    easeParams: [2.5],
  });
}

/**
 * Star slam animation — used for stage clear stars.
 */
export function starSlam(
  scene: Phaser.Scene,
  star: Phaser.GameObjects.Image,
  index: number,
  earned: boolean
): void {
  const baseDelay = 300;
  const stagger = 350;
  const delay = baseDelay + index * stagger;

  star.setScale(0);
  star.setAlpha(0);

  scene.tweens.add({
    targets: star,
    scaleX: earned ? 1.4 : 0.9,
    scaleY: earned ? 1.4 : 0.9,
    alpha: 1,
    duration: 350,
    delay,
    ease: 'Back.easeOut',
    easeParams: [4],
    onComplete: () => {
      if (earned) {
        scene.tweens.add({
          targets: star,
          scaleX: 1,
          scaleY: 1,
          duration: 200,
          ease: 'Sine.easeOut',
        });
        scene.time.delayedCall(250, () => {
          scene.tweens.add({
            targets: star,
            scaleX: 1.1,
            scaleY: 1.1,
            duration: 600,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
          });
        });
        // Prefer ascending star chime by index, fall back to legacy star earn
        const chimeKey = [
          'sfx_star_chime_1', 'sfx_star_chime_2', 'sfx_star_chime_3',
        ][Math.min(index, 2)];
        const sfxKey = scene.cache.audio.exists(chimeKey) ? chimeKey : 'sfx_star_earn';
        AudioManager.getInstance().playSFX(scene, sfxKey, { volume: 0.7 });

        // Gold burst particles on earn
        const { x, y } = star;
        for (let i = 0; i < 12; i++) {
          const angle = (Math.PI * 2 * i) / 12;
          const dist = 80;
          const particle = scene.add.circle(x, y, 6, COLORS.goldLight);
          particle.setDepth(10000);
          scene.tweens.add({
            targets: particle,
            x: x + Math.cos(angle) * dist,
            y: y + Math.sin(angle) * dist,
            alpha: 0,
            scale: 0.3,
            duration: 700,
            ease: 'Power2.easeOut',
            onComplete: () => particle.destroy(),
          });
        }
      }
    },
  });
}

// ─── Decorative Animations ───────────────────────────────────────────────────

/**
 * Continuous shimmer sweep across a target (light reflection effect).
 *
 * v4 note: AddEffectShine builds + drives its own internal tween (sweep across
 * the target). We don't need to manage the tween manually anymore — pass the
 * timing into the action config. The returned tween defaults to a single play;
 * we flip it to infinite loop by setting `loop = -1` after creation.
 */
export function shimmerSweep(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.Image,
  interval = 3000
): void {
  void scene;
  try {
    const [result] = Phaser.Actions.AddEffectShine([target], {
      duration: 800,
      yoyo: true,
      repeatDelay: interval,
      ease: 'Sine.easeInOut',
    });
    if (result?.tween) {
      result.tween.loop = -1;
    }
  } catch {
    // shine effect not available — fail silently
  }
}

/**
 * Continuous glow pulse.
 */
export function pulseGlow(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite,
  color: number = COLORS.gold,
  strength = 6
): Phaser.Filters.Glow | null {
  target.enableFilters();
  // v4 inserts `scale` (4th arg) before `knockout`. Original v3 args:
  //   (color, outerStrength=0, innerStrength=0, knockout=false, quality=0.1, distance=strength*2)
  const glow = target.filters?.internal.addGlow(color, 0, 0, 1, false, 0.1, strength * 2) ?? null;
  if (glow) {
    scene.tweens.add({
      targets: glow,
      outerStrength: strength,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }
  return glow;
}

/**
 * Gentle floating bob animation.
 */
export function floatBob(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject,
  amplitude = 8,
  duration: number = MOTION.dur.idleFloat
): void {
  const baseY = (target as any).y;
  scene.tweens.add({
    targets: target,
    y: baseY - amplitude,
    duration,
    yoyo: true,
    repeat: -1,
    ease: MOTION.ease.floaty,
  });
}

/**
 * Fade-in slide up animation for staggered elements.
 */
export function fadeSlideIn(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject,
  delay = 0,
  duration = 400,
  slideDistance = 30
): void {
  (target as any).setAlpha?.(0);
  const baseY = (target as any).y || 0;
  (target as any).setY?.(baseY + slideDistance);

  scene.tweens.add({
    targets: target,
    y: baseY,
    alpha: 1,
    duration,
    delay,
    ease: 'Power2',
  });
}

// ─── Scene Transitions ───────────────────────────────────────────────────────

import { TransitionSystem, TransitionType } from './TransitionSystem';

export function transitionTo(
  scene: Phaser.Scene,
  targetScene: string,
  data?: object,
  duration = 400,
  color = 0x000000,
  type: TransitionType = 'fade',
): void {
  TransitionSystem.to(scene, targetScene, { type, duration, color, data });
}

export function resetTransitionLock(scene: Phaser.Scene): void {
  (scene as any)._transitioning = false;
}

export function fadeIn(scene: Phaser.Scene, duration = 500): void {
  scene.cameras.main.fadeIn(duration, 0, 0, 0);
}

export function screenFlash(
  scene: Phaser.Scene,
  duration = 150,
  color = 0xFFFFFF
): void {
  scene.cameras.main.flash(duration, (color >> 16) & 0xFF, (color >> 8) & 0xFF, color & 0xFF);
}
